import { useEffect, useRef, useState } from "react";
import { Users } from "lucide-react";
import {
  useMailContacts,
  type MailRecipientSuggestion,
} from "@/hooks/useMailContacts";
import { expandContactGroupToAddressField } from "@/lib/mail/contactGroupExpand";
import { mailContactDisplayLabel } from "@/lib/mail/contactLabels";
import {
  activeAddressToken,
  replaceActiveAddressToken,
} from "@/lib/mail/parseAddressField";
import { cn } from "@/lib/cn";

type Props = {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (next: string) => void;
  className?: string;
};

export function RecipientInput({ label, value, placeholder, onChange, className }: Props) {
  const { searchRecipientSuggestions } = useMailContacts();
  const inputRef = useRef<HTMLInputElement>(null);
  const [caret, setCaret] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const token = activeAddressToken(value, caret);
  const suggestions = token.length >= 1 ? searchRecipientSuggestions(token, 8) : [];

  useEffect(() => {
    setActiveIndex(0);
  }, [token, suggestions.length]);

  function pickSuggestion(item: MailRecipientSuggestion) {
    const formatted =
      item.kind === "group"
        ? expandContactGroupToAddressField(item.group)
        : (() => {
            const email = item.contact.email;
            const displayLabel = mailContactDisplayLabel(item.contact);
            return displayLabel && displayLabel !== email
              ? `${displayLabel} <${email}>`
              : email;
          })();

    if (!formatted.trim()) return;

    const next = replaceActiveAddressToken(value, caret, formatted);
    onChange(next);
    setCaret(next.length);
    setOpen(false);
    window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.length, next.length);
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter" && suggestions[activeIndex]) {
      event.preventDefault();
      pickSuggestion(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="relative min-w-0 flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setOpen(true);
        }}
        onFocus={(e) => {
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setOpen(true);
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={handleKeyDown}
        onClick={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
        onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
        className={className}
      />

      {open && suggestions.length > 0 ? (
        <ul
          className="absolute left-0 right-0 top-full z-[100] mt-1 max-h-52 overflow-y-auto rounded-[var(--mail-radius-card)] border border-[var(--mail-border-soft)] bg-[var(--mail-surface-elevated)] py-1 shadow-[var(--mail-shadow-md)]"
          role="listbox"
        >
          {suggestions.map((item, index) => {
            if (item.kind === "group") {
              const count = item.group.memberCount;
              return (
                <li key={`g-${item.group.id}`} role="option" aria-selected={index === activeIndex}>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition hover:bg-[var(--mail-surface-hover)]",
                      index === activeIndex && "bg-[var(--mail-surface-hover)]"
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickSuggestion(item)}
                  >
                    <Users
                      className="mt-0.5 h-4 w-4 shrink-0 text-[var(--mail-accent)]"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-[var(--mail-text)]">
                        {item.group.name}
                      </span>
                      <span className="block text-xs text-[var(--mail-muted)]">
                        Groupe · {count} membre{count > 1 ? "s" : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            }

            const title = mailContactDisplayLabel(item.contact);
            return (
              <li key={`c-${item.contact.id}`} role="option" aria-selected={index === activeIndex}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full flex-col items-start px-3 py-2 text-left text-sm transition hover:bg-[var(--mail-surface-hover)]",
                    index === activeIndex && "bg-[var(--mail-surface-hover)]"
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSuggestion(item)}
                >
                  <span className="font-medium text-[var(--mail-text)]">{title}</span>
                  <span className="text-xs text-[var(--mail-muted)]">{item.contact.email}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
